import { useEffect, useState } from 'react';
import cx from 'classnames';
import type { MapCameraChangedEvent, MapEvent } from '@vis.gl/react-google-maps';
import { APIProvider, ControlPosition, Map } from '@vis.gl/react-google-maps';
import getConfig from 'next/config';

import { triggerUserDraggedOnMapEvent } from 'Services/analytics/events/vehicleLocationMapEvents';
import { reportZoomEvent } from 'Utilities/hooks/googleMaps/useGoogleMap.helpers';

import { CustomMapControls } from '../Components/CustomMapControls/CustomMapControls';
import { INIT_ZOOM_LEVEL } from '../SwitchableMap.consts';
import { getSellerLocation } from '../SwitchableMap.helpers';

import { DEFAULT_MAP_OPTIONS } from './DynamicMap.consts';
import { onZoomChanged, useDynamicMapBounds } from './DynamicMap.helpers';
import type { DynamicMapProps } from './DynamicMap.types';
import { Markers } from './Markers';

import styles from './DynamicMap.module.scss';

const { publicRuntimeConfig } = getConfig();
const { GOOGLE_MAPS_API, GOOGLE_MAPS_ID } = publicRuntimeConfig;

export const DynamicMap: React.FC<DynamicMapProps> = ({
	distance,
	enquiryId,
	initialZoomLevel,
	isDealerMarkerVisible,
	isInitiallyExpanded,
	isMapLoaded,
	locations,
	onMapError,
	onMapLoaded,
}) => {
	const defaultBounds = useDynamicMapBounds({ distance, isDealerMarkerVisible, locations });
	const [shouldRender, setShouldRender] = useState(false);
	const [shouldFireAfterTitlesLoaded, setShouldFireAfterTitlesLoaded] = useState(false);
	const [zoomLevel, setZoomLevel] = useState(INIT_ZOOM_LEVEL);

	// Internally this package using useLayoutEffect https://github.com/visgl/react-google-maps/issues/381
	useEffect(() => {
		setShouldRender(true);
	}, []);

	const sellerLocation = getSellerLocation(locations.seller);

	const handleOnZoomChanged = ({ isUserEvent, map }: { isUserEvent: boolean; map: MapCameraChangedEvent['map'] }) => {
		const isZoomChangeFromLibrary = !isMapLoaded && !isUserEvent;
		if (isZoomChangeFromLibrary) return;
		const currentZoomLevel = map.getZoom() ?? 0;
		setZoomLevel(currentZoomLevel);
		if (shouldFireAfterTitlesLoaded) {
			reportZoomEvent({ eventId: enquiryId, lastZoomLevel: zoomLevel, zoomLevel: currentZoomLevel });
			onZoomChanged({
				Circle: google.maps.Circle,
				location: sellerLocation,
				map,
			});
		}
	};

	const setInitialZoomLevel = (map: google.maps.Map) => {
		const currentZoom = map.getZoom();
		if (!currentZoom || isMapLoaded) return;
		if (initialZoomLevel === 'ZOOM_IN') {
			map.setZoom(currentZoom + 1);
		}
		if (initialZoomLevel === 'ZOOM_OUT') {
			map.setZoom(currentZoom - 1);
		}
		if (initialZoomLevel === 'NO_ZOOM') {
			map.setZoom(currentZoom);
		}
		handleOnZoomChanged({ isUserEvent: true, map });
	};

	const onTilesLoaded = (event: MapEvent<unknown>) => {
		setInitialZoomLevel(event.map);
		setShouldFireAfterTitlesLoaded(true);
		onMapLoaded();
	};

	return (
		<div className={cx([styles.wrapper])} data-testid="dynamic-map">
			<APIProvider apiKey={GOOGLE_MAPS_API} onError={onMapError}>
				{shouldRender ? (
					<>
						<Map
							{...DEFAULT_MAP_OPTIONS}
							className={styles.map}
							defaultBounds={defaultBounds}
							mapId={GOOGLE_MAPS_ID}
							onDragend={() => triggerUserDraggedOnMapEvent('dynamic_map')}
							onTilesLoaded={onTilesLoaded}
							onZoomChanged={(event) => handleOnZoomChanged({ isUserEvent: false, map: event.map })}
						/>
						<Markers isDealerMarkerVisible={isDealerMarkerVisible} locations={locations} />

						<CustomMapControls
							expandPosition={ControlPosition.TOP_RIGHT}
							isInitiallyExpanded={isInitiallyExpanded}
							isMapLoaded={isMapLoaded}
							setZoomLevel={setZoomLevel}
							zoomLevel={zoomLevel}
							zoomPosition={ControlPosition.INLINE_END_BLOCK_END}
						/>
					</>
				) : null}
			</APIProvider>
		</div>
	);
};
